/**
 * OperatingCommandBar · Jul 2026
 * ================================================================
 * A slim, always-visible Operating-mode action strip. Renders as a
 * bottom-docked bar on mobile (safe-area friendly) and a top strip
 * on desktop that sits BELOW the app nav and ABOVE the page hero.
 *
 * 3 highest-frequency Operating actions (Jun 2026 founder-requested
 * order; Plan P&L + Experiments tiles removed — both remain reachable
 * via the ⌘K palette):
 *   1. What's due        · /compliance?business={id}
 *   2. Log actuals       · /business/{id}/log-actuals
 *   3. Weekly Review     · /business/{id}/weekly-review
 *
 * (Jun 2026 · the "Log snapshot" tile was removed per founder request —
 * snapshots remain reachable via the ⌘K palette and the Here-pill
 * "Log month" shortcut.)
 *
 * Also renders a ⌘K hint that pops the CommandPalette.
 */
import { Link, useLocation } from "react-router-dom";
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  DollarSign,
  Bell,
  Command,
  GripVertical,
  ChevronsRight,
  ChevronsLeft,
  Plus,
} from "lucide-react";
import { operatingHealthPct } from "../../lib/operatingHealth";
import { useHubPrefs } from "../../lib/useHubPrefs";
import { isCurrentOperatingRoute } from "../../lib/operatingBarRoutes";
import TrajectorySparkline from "./TrajectorySparkline";
import { dueAttentionCount, roadmapDueAttentionCount, grantsDueAttentionCount } from "../DueSoonBanner";
import WhatsDuePanel from "./WhatsDuePanel";

const ITEMS = [
  {
    key: "whats-due",
    label: "What's due",
    icon: Bell,
    color: "#F59E0B",
    toBuilder: (id) => `/compliance?business=${id}`,
  },
  {
    key: "log-revenue",
    label: "Log actuals",
    icon: DollarSign,
    color: "#10B981",
    // Aug 2026 · Log revenue routes founders to the dedicated
    // <LogActualsPage /> (Monthly Check-In).
    // Feb 2027 (revision) · Founders asked for every "Log actuals"
    // entry-point to land on the standalone `/log-actuals` page —
    // NOT the in-toolbar <LogThisMonthModal /> modal card. Keeping
    // `toBuilder` here forces the render path to take the <Link>
    // branch and navigate directly. `?legFix=` is preserved so the
    // <LegFixReturnBanner /> "Fixed it · Re-run Health check" loop
    // survives.
    toBuilder: (id, qs = "") => `/business/${id}/log-actuals${qs}`,
  },
  // Feb 2027 · Weekly Review tile removed — the review now lives
  // inline on the Hub inside the "How am I doing?" section, so the
  // toolbar shortcut was redundant. Standalone route still resolves.
];

export default function OperatingCommandBar({
  businessId,
  business,
  onOpenPalette,
  onOpenLog,
  onBusinessUpdate,
}) {
  const location = useLocation();
  // Jun 2026 · What's-due badge — overdue + due-this-week open items.
  const dueCount = dueAttentionCount(business);
  // Jun 2026 · Roadmap nudges — badge also counts roadmap tasks due
  // this week / overdue so the panel's Roadmap section is reachable.
  const totalDueCount =
    dueCount + roadmapDueAttentionCount(business) + grantsDueAttentionCount(business);
  // Jun 2026 · Due-date nudges — the red count badge opens a panel to
  // snooze/reschedule due compliance items without leaving the page.
  const [duePanelOpen, setDuePanelOpen] = useState(false);
  // Aug 2026 · Preserve the `?legFix=<legKey>` query param when the
  // founder taps "Log revenue" so <LegFixReturnBanner /> keeps
  // offering the "Fixed it · Re-run Health check" one-click loop
  // on the destination /log-actuals page. Any other query params are
  // intentionally dropped — the log-actuals page owns its own state.
  const legFixQs = useMemo(() => {
    try {
      const p = new URLSearchParams(location.search || "");
      const v = p.get("legFix");
      return v ? `?legFix=${encodeURIComponent(v)}` : "";
    } catch {
      return "";
    }
  }, [location.search]);
  const isMac =
    typeof navigator !== "undefined" &&
    /Mac|iPhone|iPod|iPad/i.test(navigator.platform || "");
  const kbdLabel = isMac ? "⌘K" : "Ctrl+K";
  const score = business ? operatingHealthPct(business) : null;

  // Aug 2026 · Context-aware filtering — hide the tile that points at
  // the URL the founder is already on. Preserves the ITEMS array
  // shape (whitebox tests assert on all 6 canonical keys) but the
  // rendered lists render only what's not-current.
  const currentItem = ITEMS.find(
    (it) => isCurrentOperatingRoute(it, location.pathname, location.search, businessId),
  );
  const visibleItems = ITEMS.filter((it) => it !== currentItem);

  // Jul 2026 · position toggle. Founders can snap the desktop bar
  // between the default bottom-center perch and a top-anchored strip
  // right below the main Nav. Persisted per-user in localStorage so
  // the preference survives page reloads.
  // Aug 2026 · If the device has no localStorage entry yet AND the
  // user has set a default in Settings → Hub preferences, we honour
  // the server-side pref for the first-time-on-this-device experience.
  const [position, setPosition] = useState("bottom");
  const { hubPrefs } = useHubPrefs();
  useEffect(() => {
    try {
      const saved = localStorage.getItem("tlb:operating-bar-position");
      if (saved === "top" || saved === "bottom") {
        setPosition(saved);
        return;
      }
    } catch {
      /* no-op */
    }
    // No local override → derive from the shared hub-prefs cache
    // (no extra GET fires — singleflight across the whole app).
    if (hubPrefs === null) return; // hold until cache settles
    const serverDefault =
      hubPrefs.default_bar_position === "top" ? "top" : "bottom";
    setPosition(serverDefault);
    try {
      localStorage.setItem("tlb:operating-bar-position", serverDefault);
    } catch {
      /* no-op */
    }
  }, [hubPrefs]);
  const togglePosition = useCallback(() => {
    setPosition((prev) => {
      const next = prev === "bottom" ? "top" : "bottom";
      try {
        localStorage.setItem("tlb:operating-bar-position", next);
      } catch {
        /* no-op */
      }
      return next;
    });
  }, []);
  const desktopPositionClass =
    position === "top"
      ? "fixed top-16 left-1/2 -translate-x-1/2 z-30"
      : "fixed bottom-6 left-1/2 -translate-x-1/2 z-30";

  // Aug 2026 · left-to-right collapse. Clicking the double-chevron
  // shrinks the bar to a small pill anchored at the right edge of the
  // screen so it stops covering the page below without disappearing
  // entirely. Persisted in localStorage.
  //
  // Jul 25 2026 · Per-device persistence — the collapse state is now
  // scoped by viewport (mobile <768px vs desktop ≥768px) so hiding on
  // the phone doesn't hide it on the desktop next time. Uses a media
  // query listener to swap the active key when the viewport changes
  // mid-session (e.g. window resize, orientation flip). The legacy
  // key `tlb:operating-bar-collapsed` is read once as a migration
  // fallback so existing preferences aren't dropped on first load.
  const isMobileViewport = () => {
    try {
      return !window.matchMedia("(min-width: 768px)").matches;
    } catch {
      return false;
    }
  };
  const collapseKey = (mobile) =>
    mobile ? "tlb:operating-bar-collapsed:mobile" : "tlb:operating-bar-collapsed:desktop";
  const [mobileViewport, setMobileViewport] = useState(false);
  const [collapsed, setCollapsed] = useState(false);
  useEffect(() => {
    const readForViewport = (mobile) => {
      try {
        const scoped = localStorage.getItem(collapseKey(mobile));
        if (scoped != null) return scoped === "1";
        // Legacy migration — inherit the pre-scoped value once,
        // then let each viewport diverge naturally on next toggle.
        return localStorage.getItem("tlb:operating-bar-collapsed") === "1";
      } catch {
        return false;
      }
    };
    const mobile = isMobileViewport();
    setMobileViewport(mobile);
    setCollapsed(readForViewport(mobile));

    let mq;
    try {
      mq = window.matchMedia("(min-width: 768px)");
    } catch {
      return undefined;
    }
    const onChange = (e) => {
      const nowMobile = !e.matches;
      setMobileViewport(nowMobile);
      setCollapsed(readForViewport(nowMobile));
    };
    // `addEventListener` on modern browsers, fallback for Safari <14.
    if (mq.addEventListener) mq.addEventListener("change", onChange);
    else mq.addListener(onChange);
    return () => {
      if (mq.removeEventListener) mq.removeEventListener("change", onChange);
      else mq.removeListener(onChange);
    };
  }, []);
  const toggleCollapsed = useCallback(() => {
    setCollapsed((prev) => {
      const next = !prev;
      try {
        localStorage.setItem(collapseKey(mobileViewport), next ? "1" : "0");
      } catch {
        /* no-op */
      }
      return next;
    });
  }, [mobileViewport]);

  const renderItem = (item, testid) => {
    const Icon = item.icon;
    const commonClass =
      "group inline-flex items-center gap-2 px-3 py-2 border border-transparent hover:border-[#4A4458] hover:bg-white transition-colors";
    const inner = (
      <>
        <span
          className="relative w-6 h-6 flex items-center justify-center text-white"
          style={{ background: item.color }}
        >
          <Icon className="w-3.5 h-3.5" aria-hidden />
          {item.key === "whats-due" && totalDueCount > 0 && (
            <button
              type="button"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setDuePanelOpen((v) => !v);
              }}
              className="absolute -top-1.5 -right-1.5 min-w-[14px] h-[14px] px-0.5 flex items-center justify-center bg-[#DC2626] hover:bg-[#B91C1C] text-white text-[9px] font-black leading-none cursor-pointer"
              data-testid="operating-whats-due-count"
              aria-label={`${totalDueCount} item${totalDueCount === 1 ? "" : "s"} due or overdue — tap to snooze or reschedule`}
              title="Snooze or reschedule due items"
            >
              {totalDueCount > 9 ? "9+" : totalDueCount}
            </button>
          )}
        </span>
        <span className="text-[11px] font-black uppercase tracking-[0.16em] text-[#4A4458] whitespace-nowrap">
          {item.label}
        </span>
      </>
    );
    if (item.toBuilder) {
      return (
        <Link
          key={item.key}
          to={item.toBuilder(businessId, legFixQs)}
          className={commonClass}
          style={{ borderRadius: 0 }}
          data-testid={testid}
        >
          {inner}
        </Link>
      );
    }
    return (
      <button
        key={item.key}
        type="button"
        onClick={() => onOpenLog?.(item.action === "log-snapshot" ? "snapshot" : "revenue")}
        className={commonClass}
        style={{ borderRadius: 0 }}
        data-testid={testid}
      >
        {inner}
      </button>
    );
  };

  // Aug 2026 · "You are here" breadcrumb pill — non-clickable static
  // chip surfacing the current-page item's icon + color so founders
  // always have visual confirmation of where they are, even now that
  // the redundant tile is hidden. Rendered only when a nav-item is
  // hidden (i.e., we found a `currentItem`).
  //
  // The pill ALSO carries a page-scoped shortcut on the right — the
  // "obvious next action" from each page:
  //   • Weekly Review  → open Log Month modal (snapshot tab)
  //   • Plan P&L       → open Log Month modal (snapshot tab)
  //   • Experiments    → deep-link to /experiments?new=1 so the
  //                      ExperimentsPage auto-opens its templates modal
  // Log Actuals and Compliance skip the shortcut (the founder is
  // already doing the primary action on those pages).
  // Feb 2027 · Weekly Review retired as a standalone destination. The
  // "here shortcut" now only fires on the Cohort P&L page where the
  // founder benefits from Log-Month being one tap away.
  const renderHereShortcut = () => {
    if (!currentItem) return null;
    if (currentItem.key === "cohort-pnl") {
      return (
        <button
          type="button"
          onClick={() => onOpenLog?.("snapshot")}
          className="inline-flex items-center gap-1 pl-2 pr-2 py-1 border-l border-neutral-300 text-[10px] font-black uppercase tracking-[0.16em] text-[#4A4458] hover:bg-neutral-100 transition-colors"
          data-testid="operating-command-bar-here-shortcut"
          data-shortcut-key="log-month"
          aria-label="Log month"
        >
          <Plus className="w-3 h-3" aria-hidden />
          Log month
        </button>
      );
    }
    if (currentItem.key === "experiments") {
      return (
        <Link
          to={`/business/${businessId}/experiments?new=1`}
          className="inline-flex items-center gap-1 pl-2 pr-2 py-1 border-l border-neutral-300 text-[10px] font-black uppercase tracking-[0.16em] text-[#4A4458] hover:bg-neutral-100 transition-colors"
          data-testid="operating-command-bar-here-shortcut"
          data-shortcut-key="new-experiment"
          aria-label="New project"
        >
          <Plus className="w-3 h-3" aria-hidden />
          New project
        </Link>
      );
    }
    return null;
  };

  const renderHerePill = (testid) => {
    if (!currentItem) return null;
    const HereIcon = currentItem.icon;
    return (
      <span
        className="inline-flex items-center gap-2 border shrink-0"
        style={{ borderRadius: 0, borderColor: currentItem.color, background: "#F9FAFB" }}
        data-testid={testid}
        data-here-key={currentItem.key}
        aria-label={`You are here: ${currentItem.label}`}
      >
        <span className="inline-flex items-center gap-2 px-3 py-2">
          <span
            className="w-6 h-6 flex items-center justify-center text-white"
            style={{ background: currentItem.color }}
            aria-hidden
          >
            <HereIcon className="w-3.5 h-3.5" />
          </span>
          <span
            className="text-[11px] font-black uppercase tracking-[0.16em] whitespace-nowrap"
            style={{ color: currentItem.color }}
          >
            Here · {currentItem.label}
          </span>
        </span>
        {renderHereShortcut()}
      </span>
    );
  };

  return (
    <>
      {/* Aug 2026 · Desktop collapsed handle — small right-edge pill
          styled to match the Help + Glossary launchers on the opposite
          corner. Mobile gets its own handle below because the desktop
          position (bottom-24 right-6) collides with the mobile FAB row.
          Aug 2026 (bug fix) · Positioned at `bottom-24 right-6` so it
          stacks ABOVE the Founder Mentor launcher (which owns `bottom-6
          right-6`) — otherwise the Mentor covers the expand handle
          and the toolbar looks permanently hidden. Top-position mode
          sits at `top-6 right-6` (no conflict up there). */}
      {collapsed && (
        <button
          type="button"
          onClick={toggleCollapsed}
          className={`hidden md:inline-flex ${
            position === "top" ? "fixed top-6 right-6 z-30" : "fixed bottom-24 right-6 z-30"
          } group items-center px-2 h-12 bg-white hover:bg-neutral-900 text-neutral-900 hover:text-white border-2 border-neutral-900 shadow-[6px_6px_0_0_rgba(0,0,0,0.18)] hover:shadow-[8px_8px_0_0_rgba(0,0,0,0.24)] transition-colors rounded-none`}
          data-testid="operating-command-bar-collapsed-handle"
          aria-label="Expand operating command bar"
          title="Expand operating command bar"
        >
          <span className="w-7 h-7 flex items-center justify-center bg-[#4A4458] text-white flex-shrink-0">
            <ChevronsLeft className="w-4 h-4" aria-hidden />
          </span>
          <span className="max-w-0 opacity-0 ml-0 group-hover:max-w-[120px] group-hover:opacity-100 group-hover:ml-2 group-hover:mr-1 group-focus-visible:max-w-[120px] group-focus-visible:opacity-100 group-focus-visible:ml-2 overflow-hidden whitespace-nowrap transition-[max-width,opacity,margin] duration-300 ease-out text-xs uppercase tracking-[0.25em] font-bold">
            Ops
          </span>
        </button>
      )}

      {/* Jul 25 2026 · Mobile collapsed handle — sits where the
          expanded pill lives (bottom-40 right-6) so the expand tap
          target is exactly where founders remember the bar was.
          Uses the same safe-area treatment as the pill. */}
      {collapsed && (
        <button
          type="button"
          onClick={toggleCollapsed}
          className="md:hidden fixed bottom-40 right-6 z-30 inline-flex items-center gap-2 h-10 px-2 bg-white text-neutral-900 border-2 border-[#4A4458] shadow-[4px_4px_0px_rgba(74,68,88,1)] active:translate-y-0.5 transition-transform"
          style={{ bottom: "calc(10rem + env(safe-area-inset-bottom))", borderRadius: 0 }}
          data-testid="operating-command-bar-collapsed-handle-mobile"
          aria-label="Expand operating command bar"
          title="Expand operating command bar"
        >
          <span className="w-6 h-6 flex items-center justify-center bg-[#4A4458] text-white flex-shrink-0">
            <ChevronsLeft className="w-3.5 h-3.5" aria-hidden />
          </span>
          <span className="text-[10px] uppercase tracking-[0.2em] font-black text-[#4A4458]">
            Ops
          </span>
        </button>
      )}


      {/* Desktop: bottom-center floating pill, constrained to
          max-w-4xl so the corner Help (bottom-6 left-6) and Founder Mentor
          (bottom-6 right-6) launchers stay reachable. Inner action row
          scrolls horizontally on narrower viewports. */}
      <div
        className={`${collapsed ? "hidden" : "hidden md:flex"} ${desktopPositionClass} max-w-[calc(100vw-14rem)] items-center gap-3 border border-[#4A4458] bg-white shadow-[4px_4px_0px_rgba(74,68,88,1)] px-3 py-2`}
        style={{ borderRadius: 0 }}
        data-testid="operating-command-bar"
        data-position={position}
        data-collapsed={collapsed ? "true" : "false"}
        aria-label="Operating command bar"
      >
        <button
          type="button"
          onClick={togglePosition}
          className="shrink-0 p-1 text-neutral-500 hover:text-[#4A4458] hover:bg-neutral-100 transition-colors"
          title={
            position === "bottom"
              ? "Snap bar to top of screen"
              : "Snap bar to bottom of screen"
          }
          data-testid="operating-command-bar-toggle-position"
          aria-label="Toggle bar position"
        >
          <GripVertical className="w-3.5 h-3.5" aria-hidden />
        </button>
        {/* Aug 2026 · left-to-right collapse trigger. Lives on the LEFT
            edge of the bar so the collapse animation feels like the
            bar folds rightward, leaving a right-edge handle. */}
        <button
          type="button"
          onClick={toggleCollapsed}
          className="shrink-0 p-1 text-neutral-500 hover:text-[#4A4458] hover:bg-neutral-100 transition-colors"
          title="Collapse operating command bar to the right"
          data-testid="operating-command-bar-toggle-collapse"
          aria-label="Collapse operating command bar"
        >
          <ChevronsRight className="w-3.5 h-3.5" aria-hidden />
        </button>
        <span className="text-[10px] font-black uppercase tracking-[0.24em] text-[#7C5FB0] whitespace-nowrap pl-1 shrink-0">
          Operating
        </span>
        <span className="w-px h-6 bg-neutral-200 shrink-0" aria-hidden />
        <div className="flex items-center gap-1 flex-1 min-w-0 overflow-x-auto">
          {renderHerePill("operating-command-bar-here")}
          {visibleItems.map((it) => renderItem(it, `operating-action-${it.key}`))}
        </div>
        <span className="w-px h-6 bg-neutral-200 shrink-0" aria-hidden />
        <div className="flex items-center gap-3 shrink-0">
          {business && (
            <div
              className="hidden lg:inline-flex items-center gap-2 px-2 py-1"
              data-testid="operating-command-bar-health"
              title={`Operating Health · ${score}/100`}
            >
              <span className="text-[10px] font-black uppercase tracking-[0.16em] text-[#4A4458]">
                Health
              </span>
              <span
                className="text-sm font-black tabular-nums text-[#7C5FB0]"
                data-testid="operating-command-bar-health-score"
              >
                {score}
              </span>
              <TrajectorySparkline
                business={business}
                width={44}
                height={20}
                testid="operating-command-bar-sparkline"
              />
            </div>
          )}
          <button
            type="button"
            onClick={onOpenPalette}
            className="inline-flex items-center gap-2 px-3 py-1.5 border border-[#4A4458] bg-white hover:bg-neutral-100 transition-colors"
            style={{ borderRadius: 0 }}
            data-testid="command-palette-trigger"
            aria-label="Open command palette"
          >
            <Command className="w-3.5 h-3.5 text-[#7C5FB0]" aria-hidden />
            <span className="text-[10px] font-black uppercase tracking-[0.16em] text-[#4A4458]">
              Search
            </span>
            <kbd className="text-[10px] font-mono text-neutral-500 border border-neutral-300 px-1">
              {kbdLabel}
            </kbd>
          </button>
        </div>
        {duePanelOpen && (
          <WhatsDuePanel
            business={business}
            businessId={businessId}
            onClose={() => setDuePanelOpen(false)}
            onBusinessUpdate={onBusinessUpdate}
            positionClass={position === "top" ? "left-0 top-full mt-2" : "left-0 bottom-full mb-2"}
          />
        )}
      </div>

      {/* Mobile: floating pill lifted above the mobile launchers row.
          Previous positioning (`bottom-20 inset-x-0`) collided with the
          bottom-24 FAB zone — Plan-Actions Menu FAB, Log-Month FAB, and
          Glossary launcher all sit at bottom-24 h-12 (96–144px), which
          punched a hole through the bar's left + right ends. Aug 2026:
          repositioned to `bottom-40` (clears the entire FAB stack) and
          converted to a centered floating pill (`left-1/2 -translate-x-1/2
          max-w-[calc(100vw-1rem)]`) so it matches the desktop pill and
          never gets obscured.
          Jul 25 2026 · Now also respects the shared `collapsed` state so
          mobile founders can dismiss the bar too (previously the mobile
          bar ignored collapse entirely). Collapsed state is persisted
          globally in localStorage. */}
      <div
        className={`${collapsed ? "hidden" : "md:hidden flex"} fixed left-1/2 -translate-x-1/2 bottom-40 z-30 border border-[#4A4458] bg-white shadow-[4px_4px_0px_rgba(74,68,88,1)] max-w-[calc(100vw-1rem)]`}
        style={{ borderRadius: 0, bottom: "calc(10rem + env(safe-area-inset-bottom))" }}
        data-testid="operating-command-bar-mobile"
        data-collapsed={collapsed ? "true" : "false"}
        aria-label="Operating command bar (mobile)"
      >
        {duePanelOpen && (
          <WhatsDuePanel
            business={business}
            businessId={businessId}
            onClose={() => setDuePanelOpen(false)}
            onBusinessUpdate={onBusinessUpdate}
            positionClass="left-0 bottom-full mb-2"
          />
        )}
        <div className="flex items-center gap-1 overflow-x-auto px-2 py-2">
          {renderHerePill("operating-command-bar-here-mobile")}
          {visibleItems.map((it) => renderItem(it, `operating-action-mobile-${it.key}`))}
          <button
            type="button"
            onClick={onOpenPalette}
            className="inline-flex items-center gap-1 px-2 py-2 border border-[#4A4458] bg-white shrink-0"
            style={{ borderRadius: 0 }}
            data-testid="command-palette-trigger-mobile"
            aria-label="Open command palette"
          >
            <Command className="w-3.5 h-3.5 text-[#7C5FB0]" aria-hidden />
            <span className="text-[10px] font-black uppercase tracking-[0.16em] text-[#4A4458]">
              {kbdLabel}
            </span>
          </button>
        </div>
        {/* Jul 25 2026 · Mobile collapse trigger — matches the desktop
            ChevronsRight affordance. Jun 2026: pinned OUTSIDE the scrolling
            row so it stays reachable on narrow phones. */}
        <button
          type="button"
          onClick={toggleCollapsed}
          className="shrink-0 px-2 text-neutral-500 hover:text-[#4A4458] hover:bg-neutral-100 transition-colors border-l border-neutral-200"
          data-testid="operating-command-bar-toggle-collapse-mobile"
          aria-label="Collapse operating command bar"
          title="Collapse operating command bar"
        >
          <ChevronsRight className="w-3.5 h-3.5" aria-hidden />
        </button>
      </div>
    </>
  );
}
